import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/LoadingSpinner';
import SEO from '../../components/SEO';
import FileUploadTab from '../bulk-email-validation/components/FileUploadTab';
import PasteEmailsTab from '../bulk-email-validation/components/PasteEmailsTab';
import ValidationProgress from '../bulk-email-validation/components/ValidationProgress';
import ValidationResults from '../bulk-email-validation/components/ValidationResults';
import RecentValidations from './components/RecentValidations';
import ValidationStats from './components/ValidationStats';
import MarketingSection from './components/MarketingSection';
import LoginSection from './components/LoginSection';
import AuthPromptModal from '../../components/AuthPromptModal';
import emailValidationService from '../../utils/emailValidationService';
import FAQ from '../../components/ui/FAQ';
import { getFAQsForPage } from '../../utils/faqContent';
import { generatePageFAQSchema } from '../../utils/faqSchema';

const EmailValidationDashboard = () => {
  const { user, refreshUserCredits } = useAuth();
  
  // Get FAQs for dashboard page
  const dashboardFAQs = getFAQsForPage('dashboard');
  const faqSchema = generatePageFAQSchema('dashboard', 'https://mailvalidator.octalchip.com/', {
    pageTitle: 'Email Validation SaaS',
    pageDescription: 'Validate single emails or bulk email lists with our professional email validation dashboard.'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('single');
  const [selectedFile, setSelectedFile] = useState(null);
  const [pastedEmails, setPastedEmails] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState({
    total: 0,
    processed: 0,
    valid: 0,
    invalid: 0,
    risky: 0,
    unknown: 0,
    status: 'idle',
    timeRemaining: null,
    processingRate: 0
  });
  const [validationResults, setValidationResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [userCredits, setUserCredits] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [creditsUpdated, setCreditsUpdated] = useState(false);
  
  // Add ref to prevent multiple API calls
  const creditsLoadedRef = useRef(false);
  const creditsLoadingRef = useRef(false); // Add loading state ref

  // Single email validation states
  const [singleEmail, setSingleEmail] = useState('');
  const [singleResult, setSingleResult] = useState(null);
  const [singleError, setSingleError] = useState(null);
  const [isSingleValidating, setIsSingleValidating] = useState(false);

  const tabs = [
    {
      id: 'single',
      label: 'Single Email',
      icon: 'Mail',
      description: 'Validate individual email addresses instantly'
    },
    {
      id: 'paste',
      label: 'Paste Bulk Emails',
      icon: 'Type',
      description: 'Copy and paste multiple email addresses directly'
    },
    {
      id: 'upload',
      label: 'File Upload',
      icon: 'Upload',
      description: 'Upload CSV or TXT files containing email addresses'
    }
  ];

  const [validationStats, setValidationStats] = useState({
    totalValidated: '12,847',
    creditsRemaining: '8,153',
    validEmails: '9,156',
    invalidEmails: '2,438'
  });

  // Monitor state changes for debugging
  useEffect(() => {
    // console.log('🔍 State changed - showResults:', showResults, 'validationResults:', validationResults);
  }, [showResults, validationResults]);

  // Check auth status on component mount
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setLoading(false);
    };

    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Auth state changes handled by AuthContext
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user credits on component mount - IMPROVED VERSION
  useEffect(() => {
    const loadInitialCredits = async () => {
      // Prevent multiple calls with both loaded and loading checks
      if (user && !creditsLoadedRef.current && !creditsLoadingRef.current) {
        creditsLoadingRef.current = true; // Set loading flag immediately
        
        try {
          // console.log('🔄 Loading current credits from API...');
          
          // Get current credits from API using empty emails array
          const result = await emailValidationService.getCurrentCredits();
          
          if (result.success && result.data) {
            const currentCredits = result.data;
            setUserCredits(currentCredits);
            
            // Save to local storage for caching
            localStorage.setItem('userCredits', JSON.stringify(currentCredits));
            
            // console.log('✅ Current credits loaded from API:', currentCredits);
          } else {
            // console.error('❌ Failed to load credits from API:', result.error);
            
            // Fallback to local storage if API fails
            const localCredits = localStorage.getItem('userCredits');
            if (localCredits) {
              try {
                const parsedCredits = JSON.parse(localCredits);
                setUserCredits(parsedCredits);
                // console.log('📦 Loaded credits from local storage as fallback:', parsedCredits);
              } catch (error) {
                // console.error('Error parsing local credits:', error);
              }
            } else {
              // Set default credits if no fallback available
              const defaultCredits = {
                credits_total: 100,
                credits_used: 0,
                credits_remaining: 100,
                subscription_plan: 'free',
                billing_cycle: 'monthly',
                is_unlimited: false
              };
              setUserCredits(defaultCredits);
              // console.log('🔧 Set default credits:', defaultCredits);
            }
          }
          
          creditsLoadedRef.current = true; // Mark as loaded only after successful completion
        } catch (error) {
          // console.error('Error loading initial credits:', error);
          // Don't reset flags on error to prevent retry loops
        } finally {
          creditsLoadingRef.current = false; // Always reset loading flag
        }
      }
    };

    loadInitialCredits();
  }, [user]); // Only depend on user to prevent infinite loops

  // Reset credits loaded flag when user changes
  useEffect(() => {
    creditsLoadedRef.current = false;
  }, [user]);

  // Function to refresh credits manually
  const refreshCredits = async () => {
    if (!user || creditsLoadingRef.current) return; // Prevent multiple refresh calls
    
    try {
      // console.log('🔄 Manually refreshing credits...');
      creditsLoadingRef.current = true; // Set loading flag
      
      const result = await emailValidationService.getCurrentCredits();
      
      if (result.success && result.data) {
        const currentCredits = result.data;
        setUserCredits(currentCredits);
        
        // Save to local storage
        localStorage.setItem('userCredits', JSON.stringify(currentCredits));
        
        // console.log('✅ Credits refreshed:', currentCredits);
        
        // Show refresh indicator
        setCreditsUpdated(true);
        setTimeout(() => setCreditsUpdated(false), 3000);
      } else {
        // console.error('❌ Failed to refresh credits:', result.error);
      }
    } catch (error) {
      // console.error('Error refreshing credits:', error);
    } finally {
      creditsLoadingRef.current = false; // Always reset loading flag
    }
  };

  const handleLoginSuccess = (user) => {
    // Handle successful login
    // console.log('Login successful:', user);
  };

  const handleSingleValidate = async (e) => {
    e.preventDefault();
    if (!singleEmail.trim()) return;

    setIsSingleValidating(true);
    setSingleError(null);
    setSingleResult(null);

    try {
      const result = await emailValidationService.validateSingleEmail(singleEmail);
      if (result.success) {
        setSingleResult(result.data);
      } else {
        setSingleError(result.error || 'Validation failed');
      }
      
      // Credits are updated from API response, no need to refresh stats
      
      // Update credits from API response
      const creditsUsed = result.creditsUsed || result.data?.creditsUsed || 1;
      const creditsRemaining = result.creditsRemaining || result.data?.creditsRemaining || 0;
      const creditsTotal = result.creditsTotal || result.data?.creditsTotal || 0;
      const creditsUsedTotal = result.creditsUsedTotal || result.data?.creditsUsedTotal || 0;
      
      // console.log('Credits from single validation API response:', {
      //   creditsUsed,
      //   creditsRemaining,
      //   creditsTotal,
      //   creditsUsedTotal
      // });
      
      // Update local state with credits from API response
      const updatedCredits = {
        credits_total: creditsTotal,
        credits_used: creditsUsedTotal,
        credits_remaining: creditsRemaining,
        subscription_plan: userCredits?.subscription_plan || 'free',
        billing_cycle: userCredits?.billing_cycle || 'monthly',
        is_unlimited: userCredits?.is_unlimited || false
      };
      
      setUserCredits(updatedCredits);
      
      // Save updated credits to local storage
      localStorage.setItem('userCredits', JSON.stringify(updatedCredits));
      
      // Dispatch custom event to notify notification component
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
      
      // console.log('Updated credits from single validation API response:', updatedCredits);
    } catch (error) {
      setSingleError('An error occurred during validation');
    } finally {
      setIsSingleValidating(false);
    }
  };

  const handleFileSelect = useCallback((file) => {
    setSelectedFile(file);
    setValidationResults(null);
    setShowResults(false);
    setError(null);
  }, []);

  const handleEmailsChange = useCallback((emails) => {
    setPastedEmails(emails);
    setValidationResults(null);
    setShowResults(false);
    setError(null);
  }, []);

  const handleStartValidation = async () => {
    // console.log('=== BULK VALIDATION FLOW START ===');
    // console.log('handleStartValidation called, user:', user); // Debug log
    // console.log('User object details:', user); // Debug log
    
    // Check if user is authenticated and show custom modal
    if (!user) {
      // console.log('No user found, showing auth modal'); // Debug log
      setShowAuthModal(true);
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting) {
      // console.log('Validation already in progress, ignoring duplicate request');
      return;
    }

    // console.log('Starting bulk validation...'); // Debug log
    setError(null);
    setIsSubmitting(true);
    
    let emails = [];
    let validationType = activeTab === 'upload' ? 'file_upload' : 'bulk';
    
    if (activeTab === 'upload') {
      if (!selectedFile) {
        setError('Please select a file to upload');
        setIsSubmitting(false);
        return;
      }
      
      try {
        const fileContent = await readFileContent(selectedFile);
        const fileType = selectedFile.name.split('.').pop().toLowerCase();
        const parseResult = emailValidationService.parseEmailFile(fileContent, fileType);
        
        if (!parseResult.success) {
          setError(parseResult.error);
          setIsSubmitting(false);
          return;
        }
        
        emails = parseResult.data.emails;
        
        if (emails.length === 0) {
          setError('No valid email addresses found in the file');
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        setError('Failed to read file content');
        setIsSubmitting(false);
        return;
      }
    } else {
      emails = pastedEmails;
    }

    if (emails.length === 0) {
      setError('Please provide email addresses to validate');
      setIsSubmitting(false);
      return;
    }

    // console.log(`Preparing to validate ${emails.length} emails...`); // Debug log

    // Check credits
    if (userCredits && userCredits.credits_remaining < emails.length) {
      setError(`Insufficient credits. Required: ${emails.length}, Available: ${userCredits.credits_remaining}`);
      setIsSubmitting(false);
      return;
    }

    setIsValidating(true);
    setShowResults(false);
    setValidationProgress({
      total: emails.length,
      processed: 0,
      valid: 0,
      invalid: 0,
      risky: 0,
      unknown: 0,
      status: 'processing',
      timeRemaining: Math.floor(emails.length / 2.5),
      processingRate: 2.5
    });

    // console.log('Making SINGLE HTTP call to bulk validation endpoint...'); // Debug log
    // console.log('Emails to validate:', emails); // Debug log
    // console.log('Validation type:', validationType); // Debug log
    // console.log('Current credits before validation:', userCredits); // Debug log
    
    let result;
    try {
      result = await emailValidationService.validateBulkEmails(emails, validationType);
      // console.log('SINGLE HTTP call response:', result); // Debug log
    // console.log('Credits from response:', {
    //   creditsUsed: result?.creditsUsed || result?.data?.creditsUsed,
    //   creditsRemaining: result?.data?.creditsRemaining,
    //   creditsTotal: result?.data?.creditsTotal,
    //   creditsUsedTotal: result?.data?.creditsUsedTotal
    // }); // Debug log
    } catch (error) {
      // console.error('Error calling validateBulkEmails:', error); // Debug log
      setIsValidating(false);
      setError(`API Error: ${error.message || 'Unknown error occurred'}`);
      setIsSubmitting(false);
      return;
    }
    
    if (result && result.success) {
      // Update progress with final results
      const summary = result.data?.summary || result.data?.results || { valid: 0, invalid: 0, risky: 0, unknown: 0 };
      setValidationProgress({
        total: result.data.totalEmails,
        processed: result.data.processedEmails,
        valid: summary.valid || 0,
        invalid: summary.invalid || 0,
        risky: summary.risky || 0,
        unknown: summary.unknown || 0,
        status: 'completed',
        timeRemaining: 0,
        processingRate: result.data.processedEmails / (result.data.processingTime / 1000)
      });

      // Use email results from the response directly
      let emailResults = [];
      if (result.data.emailResults && result.data.emailResults.length > 0) {
        emailResults = result.data.emailResults;
        setValidationResults(emailResults);
        setShowResults(true);
        // console.log('✅ Setting validation results from API response:', emailResults);
        // console.log('✅ Setting showResults to true');
      } else {
        // Fallback to fetching detailed results if needed
        const detailedResults = await emailValidationService.getValidationResults(result.data.validationId, {
          page: 1,
          limit: 1000 // Load all results
        });

        if (detailedResults.success) {
          emailResults = detailedResults.data.results;
          setValidationResults(emailResults);
          setShowResults(true);
          // console.log('✅ Setting validation results from detailed results:', emailResults);
          // console.log('✅ Setting showResults to true');
        }
      }
      
      // Save validation results to local storage
      const validationRecord = {
        id: result.data.validationId,
        timestamp: new Date().toISOString(),
        totalEmails: result.data.totalEmails,
        processedEmails: result.data.processedEmails,
        results: summary,
        detailedResults: emailResults,
        creditsUsed: result.data.creditsUsed,
        processingTime: result.data.processingTime,
        validationType: validationType
      };
      
      // Get existing validation history
      const existingHistory = JSON.parse(localStorage.getItem('validationHistory') || '[]');
      existingHistory.unshift(validationRecord); // Add new record at the beginning
      
      // Keep only last 50 validations to prevent storage bloat
      const trimmedHistory = existingHistory.slice(0, 50);
      localStorage.setItem('validationHistory', JSON.stringify(trimmedHistory));
      
      // console.log('Validation results saved to local storage:', validationRecord);

      // Update credits after validation - Single API call response
      if (userCredits) {
        if (result.data.demo) {
          // For demo mode, simulate credit deduction based on email count
          const usedCredits = emails.length;
          const remainingCredits = Math.max(0, userCredits.credits_remaining - usedCredits);
          setUserCredits({
            ...userCredits,
            credits_used: userCredits.credits_used + usedCredits,
            credits_remaining: remainingCredits
          });
        } else {
          // For real validation, use updated credits from API response
          const creditsUsed = result.creditsUsed || result.data?.creditsUsed || emails.length;
          const creditsRemaining = result.creditsRemaining || result.data?.creditsRemaining || 0;
          const creditsTotal = result.creditsTotal || result.data?.creditsTotal || 0;
          const creditsUsedTotal = result.creditsUsedTotal || result.data?.creditsUsedTotal || 0;
          
          // console.log('Credits from API response:', {
          //   creditsUsed,
          //   creditsRemaining,
          //   creditsTotal,
          //   creditsUsedTotal
          // });
          
          // Update local state with credits from API response
          const updatedCredits = {
            credits_total: creditsTotal,
            credits_used: creditsUsedTotal,
            credits_remaining: creditsRemaining,
            subscription_plan: userCredits?.subscription_plan || 'free',
            billing_cycle: userCredits?.billing_cycle || 'monthly',
            is_unlimited: userCredits?.is_unlimited || false
          };
          
          setUserCredits(updatedCredits);
          
          // Save updated credits to local storage
          localStorage.setItem('userCredits', JSON.stringify(updatedCredits));
          
          // Dispatch custom event to notify notification component
          window.dispatchEvent(new CustomEvent('creditsUpdated'));
          
          // Show credits updated indicator
          setCreditsUpdated(true);
          setTimeout(() => setCreditsUpdated(false), 3000); // Hide after 3 seconds
          
          // console.log('Updated credits from API response:', updatedCredits);
        }
      }

      setIsValidating(false);
    } else {
      setIsValidating(false);
      setError(result?.error || 'Validation failed');
      setValidationProgress({
        total: 0,
        processed: 0,
        valid: 0,
        invalid: 0,
        risky: 0,
        unknown: 0,
        status: 'idle',
        timeRemaining: null,
        processingRate: 0
      });
    }
    
    // console.log('=== BULK VALIDATION FLOW END ===');
    setIsSubmitting(false);
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const handleExport = async (results, format) => {
    try {
      let content = '';
      let filename = '';
      let mimeType = '';
      
      if (format === 'csv') {
        const headers = ['Email', 'Status', 'Deliverability Score', 'Domain', 'MX Record', 'Disposable', 'Free Provider', 'Role Based', 'Error Type'];
        const csvContent = [
          headers.join(','),
          ...results.map(result => [
            result.email,
            result.status,
            result.deliverabilityScore,
            result.domain || '',
            result.mxRecord ? 'Yes' : 'No',
            result.disposable ? 'Yes' : 'No',
            result.freeProvider ? 'Yes' : 'No',
            result.roleBased ? 'Yes' : 'No',
            result.errorType || ''
          ].join(','))
        ].join('\n');
        
        content = csvContent;
        filename = `email_validation_results_${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else if (format === 'excel') {
        // For Excel, we'll use CSV format with Excel-specific headers
        const headers = ['Email', 'Status', 'Deliverability Score', 'Domain', 'MX Record', 'Disposable', 'Free Provider', 'Role Based', 'Error Type'];
        const csvContent = [
          headers.join('\t'),
          ...results.map(result => [
            result.email,
            result.status,
            result.deliverabilityScore,
            result.domain || '',
            result.mxRecord ? 'Yes' : 'No',
            result.disposable ? 'Yes' : 'No',
            result.freeProvider ? 'Yes' : 'No',
            result.roleBased ? 'Yes' : 'No',
            result.errorType || ''
          ].join('\t'))
        ].join('\n');
        
        content = csvContent;
        filename = `email_validation_results_${new Date().toISOString().split('T')[0]}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (format === 'json') {
        content = JSON.stringify(results, null, 2);
        filename = `email_validation_results_${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
      }
      
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleSaveToHistory = async () => {
    // This would save the current validation results to the user's history
    // console.log('Saving to history...');
  };

  const handleValidateAnother = () => {
    setValidationResults(null);
    setShowResults(false);
    setError(null);
    setSelectedFile(null);
    setPastedEmails([]);
    setValidationProgress({
      total: 0,
      processed: 0,
      valid: 0,
      invalid: 0,
      risky: 0,
      unknown: 0,
      status: 'idle',
      timeRemaining: null,
      processingRate: 0
    });
  };

  const canStartValidation = () => {
    if (activeTab === 'upload') {
      return selectedFile !== null;
    } else {
      return pastedEmails.length > 0;
    }
  };

  const getEmailCount = () => {
    if (activeTab === 'upload') {
      return selectedFile ? 'File selected' : 'No file selected';
    } else {
      return `${pastedEmails.length} emails ready`;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      valid: { color: 'bg-success/10 text-success', text: 'Valid' },
      invalid: { color: 'bg-error/10 text-error', text: 'Invalid' },
      risky: { color: 'bg-warning/10 text-warning', text: 'Risky' },
      unknown: { color: 'bg-gray-100 text-gray-600', text: 'Unknown' }
    };
    
    const config = statusConfig[status] || statusConfig.unknown;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getDeliverabilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const handleSingleClear = () => {
    setSingleEmail('');
    setSingleResult(null);
    setSingleError(null);
  };

  // Clear validation results when switching tabs
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Clear all validation results when switching tabs
    setValidationResults(null);
    setShowResults(false);
    setSingleResult(null);
    setSingleError(null);
    setSingleEmail(''); // Clear single email input
    setError(null);
    
    // Reset validation progress
    setValidationProgress({
      total: 0,
      processed: 0,
      valid: 0,
      invalid: 0,
      risky: 0,
      unknown: 0,
      status: 'idle',
      timeRemaining: null,
      processingRate: 0
    });
  };

  // Get validation history from local storage
  const getValidationHistory = () => {
    try {
      const history = localStorage.getItem('validationHistory');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      // console.error('Error loading validation history:', error);
      return [];
    }
  };

  // Clear validation history
  const clearValidationHistory = () => {
    localStorage.removeItem('validationHistory');
    // console.log('Validation history cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner variant="page" text="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Email Validation SaaS"
        description="Validate single emails or bulk email lists with our professional email validation dashboard. Real-time validation results, detailed analytics, and comprehensive email verification tools."
        keywords="email validation dashboard, single email validation, bulk email validation, email verification dashboard, email validation results, email analytics, email verification tools"
        canonical="https://mailvalidator.octalchip.com/"
        structuredData={faqSchema}
      />
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <Breadcrumb />
        
        {/* Hero Marketing Section */}
        <div id="dashboard-hero" className="bg-gradient-to-br from-primary-light to-white rounded-2xl p-8 md:p-12">
          <div className="text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary">
              Transform Your Email Marketing with 
              <span className="text-primary"> Professional Validation</span>
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Stop losing money on invalid emails. Our advanced validation technology helps businesses 
              improve deliverability, reduce costs, and boost campaign performance.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary">99.9%</div>
                <div className="text-sm text-text-secondary">Validation Accuracy</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary">50M+</div>
                <div className="text-sm text-text-secondary">Emails Validated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary">95%</div>
                <div className="text-sm text-text-secondary">Bounce Rate Reduction</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary">24/7</div>
                <div className="text-sm text-text-secondary">Uptime Guarantee</div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Validation Tabs - Right after the hero section */}
        <div id="email-validation" className="space-y-6">
          {/* Tab Navigation */}
          <div className="bg-surface border border-border rounded-lg">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-primary hover:border-primary/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
            
            {/* Tab Content */}
            <div className="p-6">
              {/* Single Email Validation Tab */}
              {activeTab === 'single' && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="Mail" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-text-primary">Single Email Validation</h3>
                      <p className="text-sm text-text-secondary">
                        Validate individual email addresses instantly
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSingleValidate} className="space-y-4">
                    <div className="flex space-x-3">
                      <div className="flex-1">
                        <Input
                          type="email"
                          value={singleEmail}
                          onChange={(e) => setSingleEmail(e.target.value)}
                          placeholder="Enter email address to validate"
                          disabled={isSingleValidating}
                          className="w-full"
                        />
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSingleValidating || !singleEmail.trim()}
                        className="min-w-[120px]"
                      >
                        {isSingleValidating ? (
                          <LoadingSpinner variant="button" text="Validating" size="small" />
                        ) : (
                          <>
                            <Icon name="Search" size={16} className="mr-2" />
                            Validate
                          </>
                        )}
                      </Button>
                    </div>

                    {singleError && (
                      <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                        <Icon name="AlertCircle" size={16} className="text-error" />
                        <span className="text-sm text-error">{singleError}</span>
                      </div>
                    )}

                    {singleResult && (
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-medium text-text-primary">Validation Result</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSingleClear}
                            iconName="X"
                          >
                            Clear
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">Email:</span>
                              <span className="text-sm font-medium text-text-primary">{singleResult.email}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">Status:</span>
                              {getStatusBadge(singleResult.status)}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">Deliverability Score:</span>
                              <span className={`text-sm font-medium ${getDeliverabilityColor(singleResult.deliverabilityScore)}`}>
                                {singleResult.deliverabilityScore}%
                              </span>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">Domain:</span>
                              <span className="text-sm font-medium text-text-primary">{singleResult.domain || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">MX Record:</span>
                              <span className="text-sm font-medium text-text-primary">
                                {singleResult.mxRecord ? 'Valid' : 'Invalid'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">Disposable:</span>
                              <span className="text-sm font-medium text-text-primary">
                                {singleResult.disposable ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Marketing CTA for Single Email - Only show for non-authenticated users */}
                    {singleResult && !user && (
                      <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-6 mt-6">
                        <div className="text-center space-y-4">
                          <div className="flex items-center justify-center space-x-2">
                            <Icon name="Zap" size={20} className="text-primary" />
                            <h4 className="text-lg font-semibold text-text-primary">
                              Ready for Bulk Validation?
                            </h4>
                          </div>
                          <p className="text-text-secondary">
                            Upgrade to our premium plan and validate thousands of emails with advanced features, detailed reports, and API access.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                              variant="primary"
                              onClick={() => window.location.href = '/auth/register'}
                              iconName="ArrowRight"
                              iconPosition="right"
                            >
                              Start Free Trial
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => window.location.href = '/pricing'}
                              iconName="ExternalLink"
                              iconPosition="right"
                            >
                              View Pricing
                            </Button>
                          </div>
                          <div className="flex items-center justify-center space-x-4 text-xs text-text-secondary">
                            <div className="flex items-center space-x-1">
                              <Icon name="Check" size={12} className="text-success" />
                              <span>100 Free Credits</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Icon name="Check" size={12} className="text-success" />
                              <span>No Credit Card Required</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Icon name="Check" size={12} className="text-success" />
                              <span>Cancel Anytime</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              )}

              {/* Paste Bulk Emails Tab */}
              {activeTab === 'paste' && (
                <div className="space-y-6">
                  <PasteEmailsTab 
                    onEmailsChange={handleEmailsChange}
                    isProcessing={isValidating}
                  />
                </div>
              )}

              {/* File Upload Tab */}
              {activeTab === 'upload' && (
                <div className="space-y-6">
                  <FileUploadTab 
                    onFileSelect={handleFileSelect}
                    isProcessing={isValidating}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bulk Validation Controls - Outside tab content */}
          {(activeTab === 'paste' || activeTab === 'upload') && (
            <>
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={20} className="text-error" />
                    <span className="text-error font-medium">{error}</span>
                  </div>
                </div>
              )}

              {/* Validation Controls */}
              {!isValidating && !showResults && (
                <div className="bg-surface border border-border rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium text-text-primary">Ready to Validate</h3>
                      <p className="text-sm text-text-secondary">{getEmailCount()}</p>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleStartValidation}
                        disabled={!canStartValidation() || isSubmitting}
                        iconName={isSubmitting ? "Loader" : "Play"}
                        iconPosition="left"
                      >
                        {isSubmitting ? 'Starting...' : 'Start Validation'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Validation Progress */}
          {isValidating && (
            <div className="mb-8">
              <ValidationProgress
                progress={validationProgress}
                onCancel={() => {
                  setIsValidating(false);
                  setValidationProgress({
                    total: 0,
                    processed: 0,
                    valid: 0,
                    invalid: 0,
                    risky: 0,
                    unknown: 0,
                    status: 'idle',
                    timeRemaining: null,
                    processingRate: 0
                  });
                }}
              />
            </div>
          )}

          {/* Validation Results */}
          {showResults && validationResults && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-text-primary">Validation Results</h2>
                <Button
                  variant="outline"
                  onClick={handleValidateAnother}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Validate Another List
                </Button>
              </div>
              
              <ValidationResults
                results={validationResults}
                onExport={handleExport}
                onSaveToHistory={handleSaveToHistory}
              />
            </div>
          )}
        </div>



        {/* Login Section for non-authenticated users */}
        {!user && (
          <div className="max-w-2xl mx-auto">
            <LoginSection onLoginSuccess={handleLoginSuccess} />
          </div>
        )}

        {/* Validation Statistics */}
        <div id="validation-stats">
          <ValidationStats 
            userCredits={userCredits} 
            onRefreshCredits={refreshCredits}
          />
        </div>

        {/* Recent Validation History */}
        {user && (
          <div id="recent-validations" className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Recent Validations</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={clearValidationHistory}
                iconName="Trash2"
                iconPosition="left"
              >
                Clear History
              </Button>
            </div>
            
            <div className="space-y-3">
              {getValidationHistory().slice(0, 5).map((validation, index) => (
                <div key={validation.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text-primary">
                      {validation.totalEmails} emails validated
                    </div>
                    <div className="text-xs text-text-secondary">
                      {new Date(validation.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="text-success">
                      {validation.results.valid} valid
                    </div>
                    <div className="text-error">
                      {validation.results.invalid} invalid
                    </div>
                    <div className="text-warning">
                      {validation.results.risky} risky
                    </div>
                    <div className="text-text-secondary">
                      {validation.creditsUsed} credits
                    </div>
                  </div>
                </div>
              ))}
              
              {getValidationHistory().length === 0 && (
                <div className="text-center py-8 text-text-secondary">
                  <Icon name="History" size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No validation history yet</p>
                  <p className="text-xs">Your validation history will appear here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rest of Marketing Content */}
        <div className="space-y-16">
          {/* Problem-Solution Section */}
          <div id="problem-solution" className="space-y-8">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-text-primary">
                Common Email Marketing Problems We Solve
              </h3>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Don't let these issues hurt your email marketing success. Our validation service addresses 
                the most common challenges businesses face.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">📉</div>
                <h4 className="font-semibold text-text-primary">High Email Bounce Rates</h4>
                <p className="text-sm text-text-secondary">Our validation catches invalid emails before they reach your sending infrastructure, reducing bounces by up to 95%.</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">📮</div>
                <h4 className="font-semibold text-text-primary">Poor Email Deliverability</h4>
                <p className="text-sm text-text-secondary">Identify and remove problematic email addresses that could hurt your sender reputation and deliverability.</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">💰</div>
                <h4 className="font-semibold text-text-primary">Wasted Marketing Budget</h4>
                <p className="text-sm text-text-secondary">Stop spending money on emails that will never reach their destination. Focus your budget on valid prospects.</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">⏰</div>
                <h4 className="font-semibold text-text-primary">Manual Verification Time</h4>
                <p className="text-sm text-text-secondary">Automate the tedious process of email verification. What used to take hours now takes seconds.</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">⚖️</div>
                <h4 className="font-semibold text-text-primary">Compliance Risks</h4>
                <p className="text-sm text-text-secondary">Stay compliant with GDPR and other regulations by ensuring you only contact valid, consenting email addresses.</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
                <div className="text-3xl">📈</div>
                <h4 className="font-semibold text-text-primary">Poor Campaign Performance</h4>
                <p className="text-sm text-text-secondary">Clean email lists lead to better open rates, click-through rates, and overall campaign success.</p>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div id="dashboard-features" className="space-y-8">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-text-primary">
                Powerful Features for Professional Results
              </h3>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Everything you need to validate emails at scale and improve your email marketing performance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">📧</div>
                <h4 className="font-semibold text-text-primary">Real-time Email Validation</h4>
                <p className="text-sm text-text-secondary">Instant validation with 99.9% accuracy. Check email format, domain existence, and deliverability in milliseconds.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Reduce bounce rates by up to 95%
                </div>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">⚡</div>
                <h4 className="font-semibold text-text-primary">Bulk Email Processing</h4>
                <p className="text-sm text-text-secondary">Process thousands of emails simultaneously with our powerful bulk validation engine.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Save hours of manual verification work
                </div>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">🔒</div>
                <h4 className="font-semibold text-text-primary">Secure & Compliant</h4>
                <p className="text-sm text-text-secondary">GDPR compliant with enterprise-grade security. Your data never leaves our secure infrastructure.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Stay compliant with data protection laws
                </div>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">📊</div>
                <h4 className="font-semibold text-text-primary">Detailed Analytics</h4>
                <p className="text-sm text-text-secondary">Comprehensive reports showing validation results, bounce reasons, and email quality metrics.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Make data-driven decisions for your campaigns
                </div>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">🔌</div>
                <h4 className="font-semibold text-text-primary">Easy API Integration</h4>
                <p className="text-sm text-text-secondary">Simple REST API with SDKs for all major programming languages. Integrate in minutes, not days.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Seamlessly integrate with your existing tools
                </div>
              </div>
              <div className="bg-surface border border-border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                <div className="text-3xl">🎯</div>
                <h4 className="font-semibold text-text-primary">Advanced Filtering</h4>
                <p className="text-sm text-text-secondary">Filter out disposable emails, role-based addresses, and low-quality domains automatically.</p>
                <div className="bg-primary-light text-primary text-xs font-medium px-3 py-1 rounded-full w-fit">
                  Improve email engagement rates significantly
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section - Only show for non-authenticated users */}
          {!user && (
            <div id="dashboard-cta" className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8 text-center space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Ready to Improve Your Email Marketing?
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Join thousands of businesses that trust our email validation service to improve their 
                deliverability and campaign performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.location.href = '/auth/register'}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = '/docs'}
                  iconName="ExternalLink"
                  iconPosition="right"
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                >
                  View API Docs
                </Button>
              </div>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-green-500" />
                  <span className="text-gray-600">100 Free Credits</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-green-500" />
                  <span className="text-gray-600">No Credit Card Required</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-green-500" />
                  <span className="text-gray-600">Cancel Anytime</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Custom Auth Prompt Modal */}
      <AuthPromptModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
      
      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50 mt-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about using the email validation dashboard
            </p>
          </div>
          <FAQ items={dashboardFAQs} variant="default" />
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default EmailValidationDashboard;